<template>
  <div v-for="w in weeks" :key="w.week">
    <h3 class="mt-3 title is-5">{{ $t('General.Week') }} {{ w.week }}</h3>
    <div class="table-container mb-2">
      <table class="table is-bordered is-striped is-hoverable">
        <thead>
          <!-- Row 1: days -->
          <tr>
            <th rowspan="2" class="time-col">{{ $t('General.Time') }}</th>
            <th
              v-for="(date, dateIndex) in w.dates"
              :key="'day-' + date"
              :colspan="vetsAvailability.length"
              class="has-text-centered day_separator"
            >
              {{ formatDate(date) }}
            </th>
          </tr>
                    <!-- Row 2: vets -->
          <tr>
            <template v-for="date in w.dates" :key="date">
              <th
                v-for="(vet, index) in vetsAvailability"
                :key="date + '-' + vet.vet_id"
                class="has-text-centered"
                :class="{ 'day-separator': index === vetsAvailability.length - 1 }"
              >
                {{ getInitials(vet.name) }}
              </th>
            </template>
          </tr>
        </thead>
        <!-- Table body cells -->
        <tbody>
          <tr v-for="time in allTimes" :key="time">
            <td>{{ time }}</td>
            <template v-for="date in w.dates" :key="date">
              <td
                v-for="(vet, index) in vetsAvailability"
                :key="date + '-' + vet.vet_id"
                :class="{ 'day-separator': index === vetsAvailability.length - 1 }"
              >
                <div class="slot-cell-vertical">
                  <div
                    v-for="bar in getSlotCoverage(vet, date, time)"
                    :key="bar.start"
                    class="slot-bar-vertical"
                    :style="{ top: bar.start + '%', height: bar.height + '%' }"
                  ></div>
                </div>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, PropType, getCurrentInstance } from "vue";
import * as XLSX from "xlsx";

interface Week {
  week: number;
  dates: string[];
}

interface WorkingInterval {
  start: string; // "09:30:00"
  end: string;   // "11:15:00"
}

interface AvailabilityInterval {
  dateStartInclusive: string;
  dateEndInclusive: string;
  hours: WorkingInterval[];
}

interface AvailabilityConfig {
  id: number;
  name: string;
  durationMinutes: number;
  availabilityStep: number;
  timeAvailableFor: string;
  description: string;
  location: string;
  workHours: { daySchedules: any[]; timezone: string };
  availabilityIntervals: { intervals: AvailabilityInterval[] };
  context: string;
  startConference: boolean;
}

interface Vet {
  vet_id: number;
  name: string;
  availability_config: AvailabilityConfig;
}

export default defineComponent({
  name: "VetAvailabilityTable",
  props: {
    vetsAvailability: {
      type: Array as PropType<Vet[]>,
      required: true,
    },
  },
  setup(props)
  {
    
    function getISOWeek(date: Date)
    {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7; // Monday = 1, Sunday = 7
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

    const weeks = computed<Week[]>(() =>
    {
      const map = new Map<number, string[]>();

      allDates.value.forEach(dateStr => {
        const d = new Date(dateStr);
        const week = getISOWeek(d);
        if (!map.has(week)) map.set(week, []);
        map.get(week)!.push(dateStr);
      });

      map.forEach(dates => dates.sort());

      return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([week, dates]) => ({ week, dates }));
    });

    const expandedDates = ref<Set<string>>(new Set());

    function toMinutes(time: string): number {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    }

    function formatDate(date: string): string
    {
      const d = new Date(date);
      const weekdayIndex = d.getDay(); // 0 = Sunday, 1 = Monday, ...
      const weekdayKey = `Weekdays.${weekdayIndex}`;
      return `${t(weekdayKey)} ${date}`;
    }

    // Collect all unique dates
    const allDates = computed(() => {
      const dates = new Set<string>();
      props.vetsAvailability.forEach((vet) => {
        vet.availability_config.availabilityIntervals.intervals.forEach((interval) => {
          dates.add(interval.dateStartInclusive.split("T")[0]);
        });
      });
      return Array.from(dates).sort();
    });

    // Build global hourly grid
    const allTimes = computed(() => {
      let minTime = 24 * 60;
      let maxTime = 0;

      props.vetsAvailability.forEach((vet) => {
        vet.availability_config.availabilityIntervals.intervals.forEach((interval) => {
          interval.hours.forEach((h) => {
            minTime = Math.min(minTime, toMinutes(h.start));
            maxTime = Math.max(maxTime, toMinutes(h.end));
          });
        });
      });

      const times: string[] = [];
      for (let t = Math.floor(minTime / 60) * 60; t <= Math.ceil(maxTime / 60) * 60; t += 60) {
        const hh = String(Math.floor(t / 60)).padStart(2, "0");
        times.push(`${hh}:00`);
      }
      return times;
    });

    function getSlotCoverage(vet: Vet, date: string, time: string) {
      const slotStart = toMinutes(time);
      const slotEnd = slotStart + 60;
      const bars: { start: number; height: number }[] = [];

      vet.availability_config.availabilityIntervals.intervals.forEach((interval) => {
        const intervalDate = interval.dateStartInclusive.split("T")[0];
        if (intervalDate !== date) return;

        interval.hours.forEach((h) => {
          const hStart = toMinutes(h.start);
          const hEnd = toMinutes(h.end);

          const overlapStart = Math.max(slotStart, hStart);
          const overlapEnd = Math.min(slotEnd, hEnd);

          if (overlapStart < overlapEnd) {
            const startPercent = ((overlapStart - slotStart) / 60) * 100;
            const heightPercent = ((overlapEnd - overlapStart) / 60) * 100;
            bars.push({ start: startPercent, height: heightPercent });
          }
        });
      });

      return bars;
    }

    function toggleDate(date: string) {
      if (expandedDates.value.has(date)) {
        expandedDates.value.delete(date);
      } else {
        expandedDates.value.add(date);
      }
    }

    function isDateExpanded(date: string) {
      return expandedDates.value.has(date);
    }

    function getInitials(name: string): string {
      return name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .join('');
    }

    const instance = getCurrentInstance();
    if (!instance) throw new Error("No Vue instance found");

    const t = (instance.proxy as any).$t as (key: string) => string;

    function exportToExcel()
    {
      const workbook = XLSX.utils.book_new();
      const worksheet: XLSX.WorkSheet = {};
      let rowIndex = 0;

      const translatedAvailability = t("VetData.VetsAvailability"); // translated title
      const timeHeader = t("General.Time");

      weeks.value.forEach((w) => {
        // === WEEK HEADER ===
        worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })] = {
          v: `${t("General.Week")} ${w.week}`,
          t: "s",
        };
        rowIndex++;

        // === ROW 1: Dates ===
        let colIndex = 1; // column 0 reserved for "Time"
        w.dates.forEach((date) => {
          const startCol = colIndex;
          const endCol = colIndex + props.vetsAvailability.length - 1;

          worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: startCol })] = {
            v: formatDate(date),
            t: "s",
          };

          if (!worksheet["!merges"]) worksheet["!merges"] = [];
          worksheet["!merges"].push({
            s: { r: rowIndex, c: startCol },
            e: { r: rowIndex, c: endCol },
          });

          colIndex += props.vetsAvailability.length;
        });
        rowIndex++;

        // === ROW 2: Vet initials ===
        colIndex = 1;
        w.dates.forEach((date) => {
          props.vetsAvailability.forEach((vet) => {
            worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })] = {
              v: getInitials(vet.name),
              t: "s",
            };
            colIndex++;
          });
        });
        rowIndex++;

        // === TIME COLUMN HEADER ===
        worksheet[XLSX.utils.encode_cell({ r: rowIndex - 2, c: 0 })] = { v: timeHeader, t: "s" }; // for first row in week block
        worksheet[XLSX.utils.encode_cell({ r: rowIndex - 1, c: 0 })] = { v: "", t: "s" }; // empty under Time

        // === BODY: Time slots ===
        allTimes.value.forEach((time) => {
          worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })] = { v: time, t: "s" }; // time column
          colIndex = 1;

          w.dates.forEach((date) => {
            props.vetsAvailability.forEach((vet) => {
              const bars = getSlotCoverage(vet, date, time);
              let coverage = 0;

              if (bars.length > 0) {
                coverage = bars.reduce((sum, b) => sum + b.height, 0) / 100; // fraction of hour
              }

              worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })] = {
                v: coverage,
                t: "n",
              };
              colIndex++;
            });
          });

          rowIndex++;
        });

        // === Add empty row between weeks for spacing ===
        rowIndex++;
      });

      // Compute max columns
      let maxCols = 1; // column 0 for Time
      weeks.value.forEach((w) => {
        const colsForWeek = w.dates.length * props.vetsAvailability.length + 1; // +1 for Time
        if (colsForWeek > maxCols) maxCols = colsForWeek;
      });

      // Define worksheet range
      worksheet["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: rowIndex - 1, c: maxCols - 1 },
      });

      // Append sheet
      XLSX.utils.book_append_sheet(workbook, worksheet, translatedAvailability);

      // === Generate unique filename with date-time prefix ===
      const now = new Date();
      const dateTimeString =
        now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, "0") +
        now.getDate().toString().padStart(2, "0") +
        now.getHours().toString().padStart(2, "0") +
        now.getMinutes().toString().padStart(2, "0") +
        now.getSeconds().toString().padStart(2, "0");

      XLSX.writeFile(workbook, `${dateTimeString}_${translatedAvailability}.xlsx`);
    }

    return {  weeks, allDates, allTimes, getSlotCoverage, toggleDate, isDateExpanded, formatDate, getInitials, exportToExcel};

  },
});
</script>

<style scoped>
.table-container {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.table-wrapper {
  overflow-x: auto;
}

.date-row {
  cursor: pointer;
}

.date-col {
  min-width: 140px;
  width: 140px;
}

.time-col {
  min-width: 70px;
  width: 70px;
}

.slot-cell-vertical {
  position: relative;
  width: 20px;
  height: 30px; /* keep it compact like appointment rows */
  margin: 0 auto;
  background: #fafafa;
}

.slot-bar-vertical {
  position: absolute;
  left: 2px;
  right: 2px;
  background: #48c774; /* Bulma green */
  border-radius: 2px;
}

thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 3;
  white-space: nowrap;
}

thead tr:first-child th {
  top: 0;
}
thead tr:nth-child(2) th {
  top: 30px; /* height of first row */
}

.day-separator {
  border-right: 3px solid #000; /* black, thicker than normal */
}

</style>
